import { Box, TableCell, Typography } from "@mui/material";
import { isDateAfterOrSameDay, isDateInCurrentMonth, isDateInCurrentWeek, isDateInPreviousMonth, isDateInPreviousWeek } from "../../../../../utils/dateUtils";
import { ServiceProvided, TreatmentPrescriptionSession } from "../../types";
import { activeServicesProvidedFilter } from "../../utils";
import { SingleValueCustomChip } from "../../../../../components/chips/SingleValueCustomChip";

type AppointmentFulfillmentTableCellProps = {
	currentServiceSessions: Array<TreatmentPrescriptionSession> | undefined;
	serviceProvided: ServiceProvided;
};

export const AppointmentFulfillmentTableCell = ({
	currentServiceSessions,
	serviceProvided,
}: AppointmentFulfillmentTableCellProps) => {
	const isServiceProvidedActive = activeServicesProvidedFilter(serviceProvided);
	if (!isServiceProvidedActive) return <TableCell></TableCell>;
	if (serviceProvided.session_periodicity === "not_applicable") {
		return (
			<TableCell align="center">
				<SingleValueCustomChip text="Não Aplicável" />
			</TableCell>
		);
	}

	const fulfilledSessiosOnThisServiceByPeriodicity: number = countSessionsOnPeriodicityCurrently(
		currentServiceSessions,
		serviceProvided.session_periodicity
	);

	const displayAppointmentFullfillment =
		!serviceProvided.date_end || isDateAfterOrSameDay(serviceProvided.date_end, new Date())
			? `${fulfilledSessiosOnThisServiceByPeriodicity} / ${serviceProvided.session_quantity}`
			: "";

	return (
		<TableCell align="center">
			<Box display="flex" alignItems="center" justifyContent="center">
				<Typography
					color={
						displayAppointmentFullfillment !== "" &&
						fulfilledSessiosOnThisServiceByPeriodicity === serviceProvided.session_quantity
							? "default"
							: "red"
					}
				>
					{displayAppointmentFullfillment}
				</Typography>
			</Box>
		</TableCell>
	);
};

export const countSessionsOnPeriodicityCurrently = (
	currentServiceSessions: Array<TreatmentPrescriptionSession> | undefined,
	periodicity: ServiceProvided["session_periodicity"]
) => {
	let count = 0;

	if (!currentServiceSessions) return count;
	currentServiceSessions.forEach((session) => {
		if (periodicity === "weekly" && isDateInCurrentWeek(new Date(session.start_date))) count++;
		if (periodicity === "monthly" && isDateInCurrentMonth(new Date(session.start_date))) count++;
	});
	return count;
};


type PreviousPeriodAppointmentFulfillmentTableCellProps = {
	currentServiceSessions: Array<TreatmentPrescriptionSession> | undefined;
	serviceProvided: ServiceProvided;
};

export const PreviousPeriodAppointmentFulfillmentTableCell = ({
	currentServiceSessions,
	serviceProvided,
}: PreviousPeriodAppointmentFulfillmentTableCellProps) => {
	const isServiceProvidedActive = activeServicesProvidedFilter(serviceProvided);
	if (!isServiceProvidedActive) return <TableCell></TableCell>;

	if (serviceProvided.session_periodicity === "not_applicable") {
		return (
			<TableCell align="center">
				<SingleValueCustomChip text="Não Aplicável" />
			</TableCell>
		);
	}

	if (isServiceStartedInCurrentPeriod(serviceProvided)) {
		return (
			<TableCell align="center">
				<SingleValueCustomChip text="Não Aplicável" />
			</TableCell>
		);
	}

	const fulfilledSessionsOnPreviousPeriod = countSessionsOnPreviousPeriod(
		currentServiceSessions,
		serviceProvided.session_periodicity
	);

	const displayPreviousPeriodFulfillment =
		!serviceProvided.date_end || isDateAfterOrSameDay(serviceProvided.date_end, new Date())
			? `${fulfilledSessionsOnPreviousPeriod} / ${serviceProvided.session_quantity}`
			: "";

	return (
		<TableCell align="center">
			<Box display="flex" alignItems="center" justifyContent="center">
				<Typography
					color={
						displayPreviousPeriodFulfillment !== "" &&
						fulfilledSessionsOnPreviousPeriod === serviceProvided.session_quantity
							? "default"
							: "red"
					}
				>
					{displayPreviousPeriodFulfillment}
				</Typography>
			</Box>
		</TableCell>
	);
};

export const countSessionsOnPreviousPeriod = (
	currentServiceSessions: Array<TreatmentPrescriptionSession> | undefined,
	periodicity: ServiceProvided["session_periodicity"]
) => {
	let count = 0;

	if (!currentServiceSessions) return count;
	currentServiceSessions.forEach((session) => {
		if (periodicity === "weekly" && isDateInPreviousWeek(new Date(session.start_date))) count++;
		if (periodicity === "monthly" && isDateInPreviousMonth(new Date(session.start_date))) count++;
	});
	return count;
};

const isServiceStartedInCurrentPeriod = (serviceProvided: ServiceProvided) => {
	const dateBegin = new Date(serviceProvided.date_begin);
	if (serviceProvided.session_periodicity === "weekly") return isDateInCurrentWeek(dateBegin);
	if (serviceProvided.session_periodicity === "monthly") return isDateInCurrentMonth(dateBegin);
	return false;
};
